import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { bookingAPI } from "@/lib/api";
import { toast } from "sonner";
import { 
  BookOpen,
  Loader2,
  ArrowRight,
  Check,
  X,
  RefreshCw
} from "lucide-react";

export default function PartnerBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [processing, setProcessing] = useState(null);

  const fetchBookings = useCallback(async () => {
    try {
      const response = await bookingAPI.getPartnerBookings();
      setBookings(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch partner bookings:', error.message);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchBookings();
    toast.success("Bookings refreshed");
  };

  const handleAccept = async (bookingId) => {
    setProcessing(bookingId);
    try {
      await bookingAPI.acceptBooking(bookingId);
      toast.success("Booking accepted!");
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept booking");
    } finally {
      setProcessing(null);
    }
  };

  const handleDecline = async (bookingId) => {
    if (!window.confirm("Are you sure you want to decline this booking? The customer will be refunded.")) {
      return;
    }
    
    setProcessing(bookingId);
    try {
      await bookingAPI.declineBooking(bookingId);
      toast.success("Booking declined. Customer will be refunded.");
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to decline booking");
    } finally {
      setProcessing(null);
    }
  };

  const getStatusColor = (status, paymentStatus) => {
    if (status === 'declined') return 'bg-red-100 text-red-800';
    if (status === 'cancelled') return 'bg-gray-100 text-gray-800';
    if (paymentStatus === 'paid') return 'bg-green-100 text-green-800';
    if (paymentStatus === 'pending') return 'bg-amber-100 text-amber-800';
    return 'bg-blue-100 text-blue-800';
  };

  const getStatusLabel = (status, paymentStatus) => {
    if (status === 'declined') return 'Declined';
    if (status === 'cancelled') return 'Cancelled';
    if (paymentStatus === 'paid') return 'Confirmed';
    if (paymentStatus === 'pending') return 'Awaiting Payment';
    return status.replace('_', ' ');
  };

  const canTakeAction = (booking) => {
    return booking.payment_status === 'paid' && 
           !['declined', 'cancelled'].includes(booking.booking_status) &&
           !booking.partner_accepted;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-black tracking-tight">Bookings Received</h1>
        <Button 
          variant="outline" 
          onClick={handleRefresh}
          disabled={refreshing}
          className="rounded-none gap-2"
          data-testid="refresh-bookings-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {bookings.length === 0 ? (
        <div className="text-center py-10 border border-border">
          <BookOpen className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground">No bookings yet</p>
          <p className="text-sm text-muted-foreground mt-2">
            Bookings from customers will appear here
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {bookings.map((booking) => (
            <div key={booking.id} className="border border-border p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 font-bold text-lg">
                    <span>{booking.listing_details.origin_city}</span>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    <span>{booking.listing_details.destination_city}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {booking.listing_type === 'cargo' ? 'Cargo' : 'Jet'} • {booking.listing_details.departure_date}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 text-xs font-bold uppercase ${getStatusColor(booking.booking_status, booking.payment_status)}`}>
                    {getStatusLabel(booking.booking_status, booking.payment_status)}
                  </span>
                  {booking.partner_accepted && (
                    <span className="px-3 py-1 text-xs font-bold uppercase bg-blue-100 text-blue-800">
                      Accepted
                    </span>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-border">
                <div>
                  <p className="label-text mb-1">Customer</p>
                  <p className="font-medium">{booking.shipper_name}</p>
                </div>
                <div>
                  <p className="label-text mb-1">Phone</p>
                  {booking.partner_accepted ? (
                    <p className="font-medium">{booking.shipper_phone || '-'}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">Accept to view</p>
                  )}
                </div>
                <div>
                  <p className="label-text mb-1">Email</p>
                  {booking.partner_accepted ? (
                    <p className="font-medium text-sm truncate">{booking.shipper_email || '-'}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">Accept to view</p>
                  )}
                </div>
                <div>
                  <p className="label-text mb-1">Quantity</p>
                  <p className="font-medium">{booking.quantity} {booking.listing_type === 'cargo' ? 'kg' : 'seats'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4 border-t border-border">
                <div>
                  <p className="label-text mb-1">Total Amount</p>
                  <p className="font-bold text-lg">₹{booking.total_amount.toFixed(2)}</p>
                </div>
                <div>
                  <p className="label-text mb-1">Your Earnings</p>
                  <p className="font-bold text-lg text-green-600">
                    ₹{(booking.total_amount - booking.platform_fee).toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="label-text mb-1">Platform Fee (2%)</p>
                  <p className="font-medium text-muted-foreground">₹{booking.platform_fee.toFixed(2)}</p>
                </div>
              </div>
              
              {booking.cargo_description && (
                <div className="pt-4 border-t border-border">
                  <p className="label-text mb-1">Cargo Description</p>
                  <p className="text-muted-foreground">{booking.cargo_description}</p>
                </div>
              )}

              {/* Action Buttons */}
              {canTakeAction(booking) && (
                <div className="pt-4 border-t border-border flex gap-3">
                  <Button
                    onClick={() => handleAccept(booking.id)}
                    disabled={processing === booking.id}
                    className="rounded-none bg-green-600 hover:bg-green-700"
                    data-testid={`accept-booking-${booking.id}`}
                  >
                    {processing === booking.id ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Accept Booking
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => handleDecline(booking.id)}
                    disabled={processing === booking.id}
                    className="rounded-none"
                    data-testid={`decline-booking-${booking.id}`}
                  >
                    {processing === booking.id ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <X className="w-4 h-4 mr-2" />
                    )}
                    Decline
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
